import { PacketManager } from './PacketManager'
import { MsgIDs } from '@/defines/MsgIDs'
import { hall } from '@/hall'
import * as proto from '@/proto/proto.js'

export class WebSocketClient {
  private static instance: WebSocketClient
  private socket: WebSocket | null = null
  private url: string
  private isConnected: boolean = false
  private reconnectInterval: number = 3000
  private listeners: Map<number, Array<(payload: Uint8Array) => void>> = new Map()
  seq: number = 0

  // 心跳相关
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private readonly HEARTBEAT_INTERVAL = 15000 // 15秒发一次心跳

  private constructor() {
    // this.url = 'wss://testdev20260323.big-win.one' // Default local test server
    this.url = 'wss://wssproduction2.toppkwin.com/ws'
  }

  public static getInstance(): WebSocketClient {
    if (!WebSocketClient.instance) {
      WebSocketClient.instance = new WebSocketClient()
    }
    return WebSocketClient.instance
  }

  public connect(url?: string): void {
    if (url) this.url = url
    if (
      this.socket &&
      (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)
    ) {
      return
    }

    console.log(`[WS] Connecting to ${this.url}...`)
    this.seq = 0
    this.socket = new WebSocket(this.url)
    this.socket.binaryType = 'arraybuffer' // Crucial for protobuf/binary packets

    this.socket.onopen = this.handleOpen.bind(this)
    this.socket.onmessage = this.handleMessage.bind(this)
    this.socket.onclose = this.handleClose.bind(this)
    this.socket.onerror = this.handleError.bind(this)
  }

  public getSeq() {
    this.seq = this.seq + 1
    return this.seq
  }

  public isNetConnected(): boolean {
    return this.isConnected
  }

  private connectHandlers: Array<() => void> = []

  private handleOpen(): void {
    this.isConnected = true
    console.log('[WS] Connected successfully.')
    this.startHeartbeat()
    this.connectHandlers.forEach((handler) => handler())
  }

  private startHeartbeat(): void {
    this.stopHeartbeat()
    this.heartbeatTimer = setInterval(() => {
      if (this.isConnected && this.socket) {
        // Client2SrvHeartBeatReq 无消息体，直接发空 payload
        const seq = this.getSeq()
        const packet = PacketManager.encode(seq, MsgIDs.Client2SrvHeartBeatReq, new Uint8Array(0))
        this.socket.send(packet)
        console.debug('[WS] Heartbeat sent.')
      }
    }, this.HEARTBEAT_INTERVAL)
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  /**
   * Registers a handler for when the WebSocket connects.
   */
  public onConnect(callback: () => void): void {
    if (this.isConnected) {
      callback()
    }
    this.connectHandlers.push(callback)
  }

  private handleMessage(event: MessageEvent): void {
    if (event.data instanceof ArrayBuffer) {
      const decodedInfo = PacketManager.decode(event.data)
      if (decodedInfo) {
        const { msgId, payload } = decodedInfo
        this.dispatch(msgId, payload)
      }
    } else {
      console.warn('[WS] Received non-binary message:', event.data)
    }
  }

  private handleClose(event: CloseEvent): void {
    this.isConnected = false
    this.seq = 0
    this.stopHeartbeat()
    console.warn(
      `[WS] Closed: ${event.code} ${event.reason}. Reconnecting in ${this.reconnectInterval}ms...`,
    )
    setTimeout(() => this.connect(), this.reconnectInterval)
  }

  private handleError(error: Event): void {
    this.seq = 0
    console.error('[WS] Error:', error)
  }

  /**
   * Registers a handler for a specific MsgID
   * @param msgId The message ID command
   * @param callback Function to call when this msgId arrives
   */
  public on(msgId: number, callback: (payload: Uint8Array) => void): void {
    if (!this.listeners.has(msgId)) {
      this.listeners.set(msgId, [])
    }
    this.listeners.get(msgId)!.push(callback)
  }

  /**
   * Unregisters a handler for a specific MsgID
   * @param msgId The message ID command
   * @param callback Optional specific function to remove
   */
  public off(msgId: number, callback?: (payload: Uint8Array) => void): void {
    if (!this.listeners.has(msgId)) return
    if (callback) {
      const handlers = this.listeners.get(msgId)!
      this.listeners.set(
        msgId,
        handlers.filter((cb) => cb !== callback),
      )
    } else {
      this.listeners.delete(msgId)
    }
  }

  /**
   * Dispatches a received packet payload to listeners
   */
  private dispatch(msgId: number, payload: Uint8Array): void {
    // 心跳 Ack，静默处理即可
    if (msgId === MsgIDs.Client2SrvHeartBeatAck) {
      console.debug('[WS] Heartbeat ack received.')
      return
    }

    const handlers = this.listeners.get(msgId)
    if (handlers && handlers.length > 0) {
      handlers.forEach((handler) => handler(payload))
    }

    // Bridge to hall.s2c handlers
    this.dispatchToHall(msgId, payload)
  }

  /**
   * Bridges messages to the global hall.s2c table.
   * Finds the message name from MsgIDs, decodes the payload, and calls the handler.
   */
  private dispatchToHall(msgId: number, payload: Uint8Array): void {
    const msgName = MsgIDs[msgId]
    if (!msgName) {
      if (!this.listeners.has(msgId)) {
        console.warn(`[WS] Unhandled MsgID received: ${msgId}`)
      }
      return
    }

    const handlerName = `on${msgName}`
    if ((hall.s2c as any)[handlerName]) {
      try {
        // Dynamically find the protobuf class and decode
        const decoder = (proto as any)[msgName]
        if (decoder && typeof decoder.decode === 'function') {
          const decodedMsg = decoder.decode(payload)
          console.log(`[WS] Dispatching to hall.s2c.${handlerName}:`, decodedMsg)
          ;(hall.s2c as any)[handlerName](decodedMsg)
        } else {
          console.warn(`[WS] Proto decoder not found for: ${msgName}`)
          // Fallback to raw payload if no decoder found but handler exists
          ;(hall.s2c as any)[handlerName](payload)
        }
      } catch (err) {
        console.error(`[WS] Error decoding/dispatching ${msgName}:`, err)
      }
    } else if (!this.listeners.has(msgId)) {
      console.warn(`[WS] No listener or hall.s2c handler for MsgID: ${msgId} (${msgName})`)
    }
  }

  /**
   * Sends a binary packet to the server via WebSocket.
   * Accepts either a pre-serialized Uint8Array or a plain JS object (auto-serialized via protobuf).
   * @param msgId Command ID
   * @param payload Serialized Uint8Array OR a plain JS object to be encoded via protobuf
   */
  public send(msgId: number, payload: Uint8Array | Record<string, any>): boolean {
    if (!this.isConnected || !this.socket) {
      console.error('[WS] Cannot send message: Not connected.')
      return false
    }

    let pbPayload: Uint8Array

    if (payload instanceof Uint8Array) {
      // Already serialized — use as-is
      pbPayload = payload
    } else {
      // Plain object — look up the proto encoder by MsgID name and serialize
      const msgName = MsgIDs[msgId]
      const encoder = msgName ? (proto as any)[msgName] : null
      if (encoder && typeof encoder.encode === 'function') {
        try {
          const msg = encoder.create(payload)
          pbPayload = encoder.encode(msg).finish()
        } catch (err) {
          console.error(`[WS] Failed to encode proto message ${msgName}:`, err)
          return false
        }
      } else {
        // No proto definition found — send empty payload (some messages have no body)
        console.warn(
          `[WS] No proto encoder for MsgID ${msgId} (${msgName}), sending empty payload.`,
        )
        pbPayload = new Uint8Array(0)
      }
    }

    const seq = this.getSeq()
    const packet = PacketManager.encode(seq, msgId, pbPayload)
    this.socket.send(packet)
    return true
  }

  public destroy(): void {
    this.stopHeartbeat()
    this.isConnected = false
    if (this.socket) {
      this.socket.onclose = null // 防止触发自动重连
      this.socket.close()
      this.socket = null
    }
    this.seq = 0
  }
}

export const wsClient = WebSocketClient.getInstance()
